import { useQuery } from '@tanstack/react-query';

import { ActionItem, ReviewEnvelope, ReviewMeetingStatusInfo } from '~/services/types';
import { useHttpClient } from '~/hooks/useHttpClient';
import { determineRetryAndMaybeLog } from '~/utils/errors';
import { createLogger } from '~/utils/logging';

import { useReviewParams } from './useReviewParams';

const log = createLogger('useReviewActionItemsQuery');

export const QUERY_KEY = 'reviewActionItems';

export type ReviewActionItemsMeeting = ReviewMeetingStatusInfo & {
  actionItems: ActionItem[];
};

export type ReviewActionItemsResponse = ReviewEnvelope<ReviewActionItemsMeeting>;

export function useReviewActionItems({ date, periodId }: { date: Date; periodId: string }) {
  const httpClient = useHttpClient();
  const params = useReviewParams({ date, periodId });

  const paramsQueryStr = params.toQueryString();

  const { data, error, status } = useQuery<null | ReviewActionItemsResponse>({
    queryKey: [QUERY_KEY, paramsQueryStr],
    queryFn: async () => {
      log.debug('Fetching review action items', {
        day: params.day,
        periodId: params.periodId,
        timeZone: params.timeZone ?? 'unknown',
      });
      const response = await httpClient.get<ReviewActionItemsResponse>(`/review/getActionItems?${paramsQueryStr}`);
      log.info('Fetched action items successfully', {
        meetingCount: response.meetings.length,
      });
      return response;
    },
    retry: (failureCount, error) =>
      determineRetryAndMaybeLog({ operation: 'fetch review action items', failureCount, error, log }),
    enabled: httpClient.isAuthenticated,
  });

  return { data, error, status };
}
